// Grok Text Service — calls xAI direct via server proxy for credit tracking.
// Models: grok-4.5, grok-4.3, grok-build-0.1 (see server/routes/xai.ts).
//
// Mirrors GeminiTextService: images are converted to base64 client-side so the
// server never has to fetch a blob: URL it cannot see.

import logger from '../utils/logger';
import { supabase } from '../lib/supabase';
import { optimizeImage, formatFileSize } from '../utils/imageOptimization';
import type { ChatMessage, ConversationContext } from '../types/api';

interface GrokTextResponse {
  content: string;
  context: ConversationContext;
  type?: 'text';
}

class GrokTextService {
  private apiBase: string;

  constructor() {
    this.apiBase = process.env.REACT_APP_API_BASE
      || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');
    logger.info('[GrokTextService] Initialized - all requests go through server proxy at', this.apiBase);
  }

  isConfigured(): boolean {
    return typeof window !== 'undefined';
  }

  /** Convert any image URL to an optimized base64 data URL. */
  private async toBase64DataUrl(imageUrl: string): Promise<string | null> {
    try {
      const result = await optimizeImage(imageUrl, {
        maxDimension: 2048,
        maxFileSizeMB: 4,
        preserveTransparency: false,
      });
      if (result.wasOptimized) {
        logger.info(`[GrokTextService] Image optimized: ${formatFileSize(result.originalSize)} → ${formatFileSize(result.optimizedSize)}`);
      }
      return result.dataUrl;
    } catch (e) {
      logger.warn('[GrokTextService] Failed to optimize image, attempting raw conversion:', e);
      try {
        if (imageUrl.startsWith('data:')) return imageUrl;
        if (imageUrl.startsWith('blob:') || imageUrl.startsWith('http') || imageUrl.startsWith('/')) {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          return await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              if (typeof reader.result === 'string') resolve(reader.result);
              else reject(new Error('FileReader result is not a string'));
            };
            reader.onerror = (err) => reject(err);
            reader.readAsDataURL(blob);
          });
        }
        return null;
      } catch (fetchErr) {
        logger.warn('[GrokTextService] Failed to fetch image for conversion:', imageUrl, fetchErr);
        return null;
      }
    }
  }

  async generateResponseStream(
    prompt: string,
    systemPrompt: string | null = null,
    context: ConversationContext | null = null,
    onToken?: (delta: string) => void,
    model: string = 'grok-4.3',
    imageUrls?: string[]
  ): Promise<GrokTextResponse> {
    const { data: { user } } = await supabase.auth.getUser();

    // Build context messages — convert any embedded image parts to base64
    const contextMessages: ChatMessage[] = [];
    if (context?.messages) {
      for (const msg of context.messages) {
        if (typeof msg.content === 'string') {
          contextMessages.push(msg);
        } else if (Array.isArray(msg.content)) {
          const processedContent: any[] = [];
          for (const part of msg.content) {
            if (part.type === 'text') {
              processedContent.push(part);
            } else if (part.type === 'image' && part.imageUrl) {
              const base64 = await this.toBase64DataUrl(part.imageUrl);
              if (base64) processedContent.push({ type: 'image', imageUrl: base64 });
            }
          }
          if (processedContent.length > 0) {
            contextMessages.push({ role: msg.role, content: processedContent });
          }
        }
      }
    }

    // Process direct image URLs (from upstream nodes) to base64
    const processedImageUrls: string[] = [];
    if (imageUrls && imageUrls.length > 0) {
      for (const url of imageUrls) {
        const base64 = await this.toBase64DataUrl(url);
        if (base64) {
          processedImageUrls.push(base64);
          logger.info('[GrokTextService] Processed image for vision input');
        }
      }
    }

    const streamHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    if (user?.id) {
      streamHeaders['x-user-id'] = user.id;
    }

    const res = await fetch(`${this.apiBase}/api/xai/chat/stream`, {
      method: 'POST',
      headers: streamHeaders,
      body: JSON.stringify({
        prompt,
        systemPrompt,
        model,
        imageUrls: processedImageUrls.length > 0 ? processedImageUrls : undefined,
        context: { messages: contextMessages },
      }),
    });

    if (!res.ok) {
      const errorBody = await res.text();
      logger.error('[GrokTextService] Stream request failed:', res.status, errorBody);
      throw new Error(`Grok Text API error: ${res.status} - ${errorBody}`);
    }

    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullContent = '';
    // Captured rather than thrown inline: the JSON.parse below sits in a
    // try/catch that swallows malformed chunks, so a throw from inside it would
    // be swallowed too and the stream would end "successfully" with no content.
    let streamError: string | null = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 2);

        if (!raw || raw.startsWith(':')) continue;

        let event: string | null = null;
        let dataStr = '';
        for (const line of raw.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          if (line.startsWith('data:')) dataStr += line.slice(5).trim();
        }
        if (!dataStr) continue;

        let payload: any;
        try {
          payload = JSON.parse(dataStr);
        } catch {
          continue; // Ignore malformed chunks
        }

        if (event === 'error') {
          streamError = payload.message || 'Grok Text stream error';
        } else if (event === 'done' && typeof payload.content === 'string') {
          fullContent = payload.content;
        } else if (typeof payload.delta === 'string') {
          fullContent += payload.delta;
          onToken?.(payload.delta);
        }
      }
    }

    if (streamError) {
      logger.error('[GrokTextService] Stream reported an error:', streamError);
      throw new Error(streamError);
    }

    const allMessages: ChatMessage[] = [
      ...contextMessages,
      { role: 'user', content: prompt },
      { role: 'assistant', content: fullContent },
    ];

    return {
      content: fullContent,
      context: { messages: allMessages },
      type: 'text',
    };
  }
}

const grokTextService = new GrokTextService();
export default grokTextService;
